import { pdf } from '@react-pdf/renderer';
import { Buffer } from 'buffer';
import LogRocket from 'logrocket';
import { PDFDocument, type PDFPage } from 'pdf-lib';

import { NewPdfPart, type PdfPart } from '@core/types/api/PdfPart/PdfPart';
import type { PdfTemplate } from '@core/types/api/PdfTemplate/PdfTemplate';
import type { Quote } from '@core/types/api/Quote/Quote';
import { type Base64, uint8ArrayToBase64 } from '@core/utils/base64';

import { TemplateByName } from './TemplateByName';

/**
 * partToPdfBytes renders a PdfPart to an ArrayBuffer of binary data containing
 * a PDF document, whose content consists of the part's template rendered with
 * data from the specified quote.
 */
const partToPdfBytes = async (
  pdfTemplate: PdfPart,
  quote: Quote,
  locale: string,
): Promise<ArrayBuffer> => {
  // Render the dynamic content from the template, if available.
  if (pdfTemplate.template !== null) {
    const blob = await pdf(TemplateByName({ name: pdfTemplate.template, quote, locale })).toBlob();
    return blob.arrayBuffer();
  }
  // Otherwise, use the staticContent.
  if (pdfTemplate.staticContent.length > 0) {
    return Buffer.from(pdfTemplate.staticContent, 'base64') as unknown as ArrayBuffer;
  }

  LogRocket.error('pdfTemplate part has neither template nor staticContent');
  return Buffer.from([]) as unknown as ArrayBuffer;
};

/**
 * Given a quote and a template of type 'code', this will generate a PDF and
 * return it as a base64-encoded string. If no pdfParts are provided, it will
 * render a single part from the pdfTemplate's template value.
 */
export const buildPdf = async (
  quote: Quote,
  pdfTemplate: PdfTemplate,
  locale: string,
): Promise<Base64> => {
  const pdfDoc = await PDFDocument.create();

  const parts = [...pdfTemplate.parts];
  if (parts.length === 0) {
    parts.push(NewPdfPart(pdfTemplate));
  }

  const pageArrays: PDFPage[][] = await Promise.all(
    parts.map(async (part): Promise<PDFPage[]> => {
      const pdfBytes = await partToPdfBytes(part, quote, locale);
      const tempPdfDoc = await PDFDocument.load(pdfBytes);
      // Split the tempPdfDoc into individual pages.
      return pdfDoc.copyPages(tempPdfDoc, tempPdfDoc.getPageIndices());
    }),
  );
  pageArrays.flat().forEach((page) => pdfDoc.addPage(page));
  return uint8ArrayToBase64(await pdfDoc.save());
};
